import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  AlertCircle,
  CheckCircle2,
  History,
  LifeBuoy,
  Plus,
  RefreshCw,
  Send,
  X,
} from "lucide-react";
import {
  PARTNER_GWIN_SUPPORT_TYPES,
  createMyPartnerGwinSupportRequest,
  fetchMyPartnerGwinSupportRequests,
  partnerGwinSupportTypeLabel,
} from "../../lib/partner/partnerGwinSupport";

const STATUS_STYLES = {
  open: "border-red-200 bg-red-50 text-red-700",
  acknowledged: "border-amber-200 bg-amber-50 text-amber-700",
  resolved: "border-green-200 bg-green-50 text-green-700",
  closed: "border-gray-200 bg-gray-100 text-gray-700",
};

function formatStatus(status) {
  switch (String(status || "").toLowerCase()) {
    case "acknowledged":
      return "Acknowledged";
    case "resolved":
      return "Resolved";
    case "closed":
      return "Closed";
    default:
      return "Open";
  }
}

function formatDateTime(value) {
  if (!value) {
    return "Not available";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat("en-ZA", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function StatusBadge({ status }) {
  const cleanStatus = String(status || "open").toLowerCase();

  return (
    <span
      className={[
        "inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-bold",
        STATUS_STYLES[cleanStatus] || STATUS_STYLES.open,
      ].join(" ")}
    >
      {formatStatus(cleanStatus)}
    </span>
  );
}

function TypeBadge({ requestType }) {
  return (
    <span className="inline-flex items-center rounded-full border border-orange-200 bg-orange-50 px-2.5 py-1 text-xs font-bold text-[#E9782F]">
      {partnerGwinSupportTypeLabel(requestType)}
    </span>
  );
}

export default function PartnerGwinSupportPanel() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [requestType, setRequestType] = useState("request_help");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [pagePath, setPagePath] = useState("");
  const [sending, setSending] = useState(false);

  const loadRequests = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const rows = await fetchMyPartnerGwinSupportRequests();
      setRequests(Array.isArray(rows) ? rows : []);
    } catch (loadError) {
      console.error("Partner Gwin support requests loading failed:", loadError);
      setRequests([]);
      setError(
        loadError?.message ||
          "Your Gwin support requests could not be loaded."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  const activeRequests = useMemo(
    () =>
      requests.filter((request) =>
        ["open", "acknowledged"].includes(request.status)
      ),
    [requests]
  );

  const recentRequests = useMemo(() => requests.slice(0, 5), [requests]);

  function openModal() {
    setError("");
    setNotice("");
    setRequestType("request_help");
    setSubject("");
    setMessage("");
    setPagePath("");
    setModalOpen(true);
  }

  function closeModal() {
    if (sending) {
      return;
    }

    setModalOpen(false);
    setRequestType("request_help");
    setSubject("");
    setMessage("");
    setPagePath("");
  }

  async function submitRequest(event) {
    event.preventDefault();

    const cleanSubject = String(subject || "").trim();
    const cleanMessage = String(message || "").trim();
    const cleanPagePath = String(pagePath || "").trim();

    if (cleanSubject.length < 3) {
      setError("Please enter a subject of at least 3 characters.");
      return;
    }

    if (cleanMessage.length < 10) {
      setError("Please provide a little more detail about your request.");
      return;
    }

    try {
      setSending(true);
      setError("");
      setNotice("");

      await createMyPartnerGwinSupportRequest({
        requestType,
        subject: cleanSubject,
        message: cleanMessage,
        pagePath: cleanPagePath,
        metadata: {
          submission_path:
            typeof window !== "undefined"
              ? `${window.location.pathname}${window.location.search}`
              : null,
          user_agent:
            typeof navigator !== "undefined" ? navigator.userAgent : null,
        },
      });

      setModalOpen(false);
      setRequestType("request_help");
      setSubject("");
      setMessage("");
      setPagePath("");
      setNotice("Your request has been sent to Gwin Support.");

      await loadRequests();
    } catch (submitError) {
      console.error("Partner Gwin support request failed:", submitError);
      setError(
        submitError?.message || "Your support request could not be submitted."
      );
    } finally {
      setSending(false);
    }
  }

  return (
    <>
      <section className="rounded-3xl border border-orange-100 bg-white p-6 shadow-sm sm:p-8">
        <div className="flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-start gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-orange-50">
              <LifeBuoy className="h-6 w-6 text-[#FF914D]" />
            </div>

            <div>
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#FF914D]">
                Partner · Gwin support
              </p>

              <h2 className="mt-2 text-2xl font-black text-gray-900">
                Contact Gwin Support
              </h2>

              <p className="mt-2 max-w-3xl text-sm leading-6 text-gray-600">
                Send the Gwin Admin team a bug report, help request, access issue,
                cohort query, feature request, or another support request.
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={openModal}
            className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl bg-[#FF914D] px-4 py-2.5 text-sm font-black text-white transition hover:bg-[#E9782F]"
          >
            <Plus className="h-4 w-4" />
            New support request
          </button>
        </div>

        {error && (
          <div className="mt-5 flex items-start gap-3 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {notice && (
          <div className="mt-5 flex items-start gap-3 rounded-2xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
            <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" />
            <span>{notice}</span>
          </div>
        )}

        <div className="mt-6 border-t border-gray-100 pt-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="font-black text-gray-900">Your Gwin support requests</h3>
              <p className="mt-1 text-sm text-gray-500">
                {activeRequests.length === 0
                  ? "You have no open requests with Gwin Support."
                  : activeRequests.length === 1
                    ? "1 request is still being handled."
                    : `${activeRequests.length} requests are still being handled.`}
              </p>
            </div>

            <button
              type="button"
              onClick={loadRequests}
              disabled={loading}
              className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs font-bold text-gray-700 transition hover:bg-gray-50 disabled:opacity-50"
            >
              <RefreshCw
                className={[
                  "h-4 w-4",
                  loading ? "animate-spin" : "",
                ].join(" ")}
              />
              Refresh
            </button>
          </div>

          {loading ? (
            <div className="mt-5 flex items-center gap-2 text-sm font-semibold text-gray-500">
              <RefreshCw className="h-4 w-4 animate-spin text-[#FF914D]" />
              Loading support requests…
            </div>
          ) : recentRequests.length === 0 ? (
            <div className="mt-5 rounded-2xl border border-gray-100 bg-gray-50 px-4 py-5">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-green-600" />
                <div>
                  <p className="text-sm font-bold text-gray-800">
                    No requests submitted
                  </p>
                  <p className="mt-1 text-sm text-gray-500">
                    Requests you send to Gwin Support will appear here.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="mt-5 space-y-3">
              {recentRequests.map((request) => (
                <div
                  key={request.report_id}
                  className="rounded-2xl border border-gray-100 bg-gray-50 p-4"
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-bold text-gray-900">{request.subject}</p>
                        <TypeBadge requestType={request.request_type} />
                      </div>
                      <p className="mt-1 text-xs text-gray-500">
                        {formatDateTime(request.created_at)}
                      </p>
                    </div>

                    <StatusBadge status={request.status} />
                  </div>

                  {request.page_path && (
                    <p className="mt-3 break-all text-xs font-semibold text-gray-500">
                      Area: {request.page_path}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}

          {requests.length > 5 && (
            <div className="mt-4 flex items-center gap-2 text-xs font-semibold text-gray-500">
              <History className="h-4 w-4" />
              Showing your 5 most recent Gwin support requests.
            </div>
          )}
        </div>
      </section>

      {modalOpen && (
        <div
          className="fixed inset-0 z-[150] flex items-center justify-center bg-black/40 px-4 py-6"
          role="dialog"
          aria-modal="true"
          aria-labelledby="partner-gwin-support-title"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget && !sending) {
              closeModal();
            }
          }}
        >
          <div className="max-h-[90vh] w-full max-w-xl overflow-y-auto rounded-3xl bg-white shadow-2xl">
            <div className="flex items-start justify-between gap-4 border-b border-gray-100 px-5 py-5 sm:px-6">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#FF914D]">
                  Partner · Gwin support
                </p>
                <h2
                  id="partner-gwin-support-title"
                  className="mt-2 text-xl font-black text-gray-900"
                >
                  Submit a support request
                </h2>
                <p className="mt-1 text-sm text-gray-500">
                  Choose the request type and tell the Gwin Admin team what you need.
                </p>
              </div>

              <button
                type="button"
                onClick={closeModal}
                disabled={sending}
                className="rounded-xl p-2 text-gray-500 transition hover:bg-gray-100 hover:text-gray-900 disabled:opacity-50"
                aria-label="Close support request"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={submitRequest} className="space-y-5 px-5 py-6 sm:px-6">
              {error && (
                <div
                  role="alert"
                  className="flex items-start gap-3 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
                >
                  <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <div>
                <label className="block text-sm font-black text-gray-900">
                  Request type
                </label>
                <select
                  value={requestType}
                  onChange={(event) => setRequestType(event.target.value)}
                  disabled={sending}
                  className="mt-2 w-full rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm outline-none transition focus:border-[#FF914D] focus:ring-4 focus:ring-[#FF914D]/10"
                >
                  {PARTNER_GWIN_SUPPORT_TYPES.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-black text-gray-900">
                  Subject
                </label>
                <input
                  type="text"
                  value={subject}
                  onChange={(event) =>
                    setSubject(event.target.value.slice(0, 120))
                  }
                  disabled={sending}
                  maxLength={120}
                  required
                  placeholder="For example: I need help managing a cohort"
                  className="mt-2 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none transition focus:border-[#FF914D] focus:ring-4 focus:ring-[#FF914D]/10"
                />
                <p className="mt-1 text-right text-xs text-gray-400">
                  {subject.length}/120
                </p>
              </div>

              <div>
                <label className="block text-sm font-black text-gray-900">
                  Page or area affected
                </label>
                <input
                  type="text"
                  value={pagePath}
                  onChange={(event) =>
                    setPagePath(event.target.value.slice(0, 500))
                  }
                  disabled={sending}
                  maxLength={500}
                  placeholder="For example: Cohorts, Team, Reports, or /partner/cohorts"
                  className="mt-2 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none transition focus:border-[#FF914D] focus:ring-4 focus:ring-[#FF914D]/10"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Optional. Add it when the request relates to a specific part of the Partner Portal.
                </p>
              </div>

              <div>
                <label className="block text-sm font-black text-gray-900">
                  Details
                </label>
                <textarea
                  value={message}
                  onChange={(event) =>
                    setMessage(event.target.value.slice(0, 4000))
                  }
                  disabled={sending}
                  maxLength={4000}
                  rows={7}
                  required
                  placeholder="Tell us what you need help with, what happened, and any useful context."
                  className="mt-2 w-full resize-y rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none transition focus:border-[#FF914D] focus:ring-4 focus:ring-[#FF914D]/10"
                />
                <p className="mt-1 text-right text-xs text-gray-400">
                  {message.length}/4000
                </p>
              </div>

              <div className="rounded-2xl border border-orange-100 bg-orange-50/60 p-4 text-sm leading-6 text-gray-600">
                This request is sent to the Gwin Admin Support inbox. Entrepreneur support requests sent to your Partner team remain separate.
              </div>

              <div className="flex flex-col-reverse gap-2 border-t border-gray-100 pt-5 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={closeModal}
                  disabled={sending}
                  className="rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-bold text-gray-700 transition hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={sending}
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#FF914D] px-4 py-2.5 text-sm font-black text-white transition hover:bg-[#E9782F] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {sending ? (
                    <RefreshCw className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                  {sending ? "Sending…" : "Send to Gwin"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
